import { useState, useEffect } from 'react';
import { FilterBar } from './components/FilterBar';
import { KPICards } from './components/KPICards';
import { OrderSourceChart } from './components/OrderSourceChart';
import { PaymentModeChart } from './components/PaymentModeChart';
import { OrderTypeChart } from './components/OrderTypeChart';
import { HourlySalesChart } from './components/HourlySalesChart';
import { TaxBreakdownChart } from './components/TaxBreakdownChart';
import { DiscountSuccessChart } from './components/DiscountSuccessChart';
import { useNavigate } from 'react-router-dom';
import { FOCSection } from './components/FOCSection';
import { DeletedOrdersSection } from './components/DeletedOrdersSection';
import { ReturnsSection } from './components/ReturnsSection';
import { PerformanceMetrics } from './components/PerformanceMetrics';
import { TopProductsSection } from './components/TopProductsSection';
import { CategoriesChart } from './components/CategoriesChart';
import { getDate } from "../../functions/dateFunctions";
import { postRequest } from '../../services/mainApp.service';
import { COMPANY_ADMIN } from "../../common/SetupMasterEnum.js";
import { useSelector } from "react-redux";
import { format } from "date-fns";

export default function DashboardPageTwo() {
    const navigate = useNavigate();
    const [dateRange, setDateRange] = useState({
        type: 'today',
        startDate: new Date(),
        endDate: new Date()
    });
    const [orderTypeFilter, setOrderTypeFilter] = useState('all');
    const [viewMode, setViewMode] = useState('day');
    const [loading, setLoading] = useState(false);
    const appStore = useSelector((state) => state.authReducer);
    const { RoleId, dateFrom, dateTo, branchId } = useSelector((state) => state.authReducer);
    const [dashboardData, setDashboardData] = useState(null);
    const [useMockData] = useState(false);

    console.log("dateRange", dateRange);

    // Helper function to get date range based on preset
    const getDateRangeFromPreset = (preset) => {
        const today = new Date();
        let startDate = new Date();
        let endDate = new Date();

        switch (preset) {
            case 'today':
                startDate = today;
                endDate = today;
                break;
            case 'yesterday':
                startDate = new Date(today);
                startDate.setDate(today.getDate() - 1);
                endDate = new Date(today);
                endDate.setDate(today.getDate() - 1);
                break;
            case 'week':
                startDate = new Date(today);
                startDate.setDate(today.getDate() - 7);
                endDate = today;
                break;
            case 'month':
                startDate = new Date(today);
                startDate.setMonth(today.getMonth() - 1);
                endDate = today;
                break;
            default:
                startDate = today;
                endDate = today;
        }

        return { startDate, endDate };
    };

    const fetchDashboardData = async () => {
        setLoading(true);
        try {
            let dateFromStr, dateToStr;

            // Handle different date range types
            if (typeof dateRange === 'string' || dateRange?.type) {
                const rangeType = typeof dateRange === 'string' ? dateRange : dateRange.type;

                if (rangeType === 'custom' && dateRange.startDate && dateRange.endDate) {
                    // Custom range with specific dates
                    dateFromStr = format(dateRange.startDate, 'yyyy-MM-dd');
                    dateToStr = format(dateRange.endDate, 'yyyy-MM-dd');
                } else {
                    // Preset range (today, yesterday, week, month)
                    const { startDate, endDate } = getDateRangeFromPreset(rangeType);
                    dateFromStr = format(startDate, 'yyyy-MM-dd');
                    dateToStr = format(endDate, 'yyyy-MM-dd');
                }
            } else {
                // Fallback to today
                dateFromStr = format(new Date(), 'yyyy-MM-dd');
                dateToStr = format(new Date(), 'yyyy-MM-dd');
            }

            console.log("Fetching with dates:", { dateFromStr, dateToStr });

            const response = await postRequest("PosDashboard", {
                OperationId: 1,
                CompanyId: appStore.CompanyId,
                BranchId:
                    appStore.userBranchList?.length > 0 &&
                        appStore.IsPos === true &&
                        appStore.RoleId !== COMPANY_ADMIN
                        ? appStore.userBranchList[0]?.BranchId
                        : RoleId === 14
                            ? branchId
                            : null,
                DateFrom: RoleId === 14 ? dateFrom : dateFromStr,
                DateTo: RoleId === 14 ? dateTo : dateToStr,
            });

            const res = await postRequest("Dashboard_Order", {
                OperationId: 1,
                CompanyId: appStore.CompanyId,
                BranchId:
                    appStore.userBranchList?.length > 0 &&
                        appStore.IsPos === true &&
                        appStore.RoleId !== COMPANY_ADMIN
                        ? appStore.userBranchList[0]?.BranchId
                        : RoleId === 14
                            ? branchId
                            : null,
                DateFrom: RoleId === 14 ? dateFrom : dateFromStr,
                DateTo: RoleId === 14 ? dateTo : dateToStr,
            });

            const dataSet = response?.data?.DataSet;
            const detials = res?.data.Data;

            if (dataSet || detials) {
                const transformedData = {
                    summary: dataSet.Table?.[0] || {},

                    orderTypes: (() => {
                        const orderModes = dataSet.Table11 || [];

                        if (orderModes.length > 0) {
                            return orderModes.map(item => ({
                                name: item.OrderMode || 'Unknown',
                                orders: item.OrderCount || 0,
                                avgOrder: item.AverageSales ||
                                    (item.TotalNetSales && item.OrderCount ?
                                        (item.TotalNetSales / item.OrderCount) : 0),
                                totalSales: item.TotalNetSales || 0
                            }));
                        }

                        const orderStats = dataSet.Table17 || [];
                        if (orderStats.length > 0) {
                            return orderStats.map(item => ({
                                name: item.OrderMode || 'Unknown',
                                orders: item.TotalOrders || 0,
                                avgOrder: item.AverageSales || 0,
                                totalSales: item.TotalNetSales || 0
                            }));
                        }

                        const orderModeDetails = dataSet.Table16 || [];
                        if (orderModeDetails.length > 0) {
                            // Group by order mode
                            const groupedByMode = orderModeDetails.reduce((acc, item) => {
                                const mode = item.OrderMode || 'Unknown';
                                if (!acc[mode]) {
                                    acc[mode] = {
                                        name: mode,
                                        orders: 0,
                                        totalSales: 0
                                    };
                                }
                                acc[mode].orders += item.OrderCount || 0;
                                acc[mode].totalSales += item.NetSales || 0;
                                return acc;
                            }, {});

                            return Object.values(groupedByMode).map(item => ({
                                name: item.name,
                                orders: item.orders,
                                avgOrder: item.orders > 0 ? item.totalSales / item.orders : 0,
                                totalSales: item.totalSales
                            }));
                        }
                        return [];
                    })(),

                    topProducts: (() => {
                        const productsData = dataSet.Table2 || [];

                        if (productsData.length > 0) {
                            // Calculate total sales for percentage verification
                            const totalSales = productsData.reduce((sum, item) => sum + (item.SalesAmount || 0), 0);

                            return productsData.map(item => ({
                                product: item.Product || 'Unknown Product',
                                category: item.CategoryName || 'Uncategorized',
                                quantity: item.Quantity || 0,
                                sales: item.SalesAmount || 0,
                                percentage: item.Percentage || (totalSales > 0 ? ((item.SalesAmount || 0) / totalSales) * 100 : 0)
                            }));
                        }

                        return [];
                    })(),

                    orderSources: dataSet.Table3?.map(item => ({
                        source: item.OrderSource,
                        count: item.OrderCount,
                        amount: item.NetSales
                    })) || [],

                    customerTypes: dataSet.Table6?.map(item => ({
                        type: item.CustomerType,
                        count: item.OrderCount,
                        amount: item.TotalSales,
                        percentage: item.Percentage
                    })) || [],

                    paymentModes: dataSet.Table8?.map(item => ({
                        mode: item.PaymentMode,
                        count: item.OrderCount,
                        amount: item.TotalAmount
                    })) || [],

                    categories: (() => {
                        const categoriesData = dataSet.Table9 || [];

                        if (categoriesData.length > 0) {
                            // Calculate total sales for percentage verification
                            const totalSales = categoriesData.reduce((sum, item) => sum + (item.SalesAmount || 0), 0);

                            return categoriesData.map(item => ({
                                name: item.CategoryName || 'Uncategorized',
                                sales: item.SalesAmount || 0,
                                quantity: item.Quantity || 0,
                                percentage: item.Percentage || (totalSales > 0 ? ((item.SalesAmount || 0) / totalSales) * 100 : 0)
                            }));
                        }

                        return [];
                    })(),

                    topCustomers: dataSet.Table10?.map(item => ({
                        name: item.CustomerName,
                        phone: item.PhoneNumber,
                        orders: item.OrderCount,
                        amount: item.NetSales
                    })) || [],

                    orderModes: dataSet.Table11?.map(item => ({
                        mode: item.OrderMode,
                        count: item.OrderCount,
                        amount: item.NetSales
                    })) || [],

                    avgOrders: (() => {
                        const avgData = dataSet.Table17 || [];
                        if (avgData.length > 0) {
                            return avgData.map(item => ({
                                type: item.OrderMode || 'Order',
                                avg: item.AverageSales || 0,
                                orders: item.TotalOrders || 0,
                                totalSales: item.TotalNetSales || 0
                            }));
                        }
                        return [];
                    })(),

                    waiterPerformance: (() => {
                        const waiterData = dataSet.Table12 || [];
                        return waiterData
                            .filter(item => item.OrderCount > 0)
                            .map(item => ({
                                name: item.WaiterName || 'Unknown',
                                orders: item.OrderCount || 0,
                                sales: item.NetSales || 0,
                                avgOrder: item.OrderCount && item.NetSales ? item.NetSales / item.OrderCount : 0
                            }));
                    })(),

                    riderPerformance: (() => {
                        const riderData = dataSet.Table13 || [];
                        return riderData
                            .filter(item => item.OrderCount > 0)
                            .map(item => ({
                                name: item.RiderName || 'Unknown',
                                orders: item.OrderCount || 0,
                                sales: item.NetSales || 0,
                                onTime: 95,
                                avgOrder: item.OrderCount && item.NetSales ? item.NetSales / item.OrderCount : 0
                            }));
                    })(),

                    departmentSummary: dataSet.Table14?.map(item => ({
                        name: item.DepartmentName,
                        quantity: item.Quantity,
                        amount: item.SalesAmount,
                        percentage: item.Percentage
                    })) || [],

                    orderModeDetails: dataSet.Table16?.map(item => ({
                        mode: item.OrderMode,
                        source: item.OrderSource,
                        count: item.OrderCount,
                        amount: item.NetSales
                    })) || [],

                    averageSales: dataSet.Table17?.map(item => ({
                        mode: item.OrderMode,
                        orders: item.TotalOrders,
                        amount: item.TotalNetSales,
                        average: item.AverageSales
                    })) || [],

                    hourlySales: (() => {
                        const hourlyData = dataSet.Table19 || [];

                        if (hourlyData.length > 0) {
                            const hourlyMap = new Map();

                            for (let i = 0; i < 24; i++) {
                                hourlyMap.set(i, {
                                    hour: i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`,
                                    hourNum: i,
                                    amount: 0,
                                    orders: 0,
                                    count: 0
                                });
                            }

                            hourlyData.forEach(item => {
                                const hour = item.SaleHour;
                                if (hour >= 0 && hour <= 23) {
                                    const existing = hourlyMap.get(hour);
                                    existing.amount += item.TotalSales || 0;
                                    existing.orders += item.TotalOrders || 0;
                                    existing.count += 1;
                                }
                            });

                            const processedData = Array.from(hourlyMap.values()).map(item => ({
                                hour: item.hour,
                                amount: item.amount,
                                orders: item.orders,
                                avgAmount: item.count > 0 ? item.amount / item.count : 0,
                                avgOrders: item.count > 0 ? item.orders / item.count : 0
                            }));

                            return processedData;
                        }
                        return [];
                    })(),

                    // Tax breakdown from Table21
                    taxBreakdown: (() => {
                        const taxData = dataSet.Table21 || [];

                        if (taxData.length > 0) {
                            return taxData.map(item => ({
                                name: `${item.GSTPercentage}% GST`,
                                percentage: 0, // Will be calculated in the chart component
                                amount: item.GstAmount || 0,
                                orders: item.OrderCount || 0,
                                taxRate: item.GSTPercentage || 0
                            }));
                        }

                        return [];
                    })(),

                    // Discounts from Table (summary)
                    discounts: (() => {
                        const summary = dataSet.Table?.[0] || {};
                        const discountAmount = summary.Discount || summary.discount || 0;

                        if (discountAmount > 0) {
                            return [
                                {
                                    name: 'Percentage Discount',
                                    successRate: 75,
                                    orders: Math.ceil(discountAmount / 500) || 1,
                                    amount: discountAmount * 0.6
                                },
                                {
                                    name: 'Fixed Amount',
                                    successRate: 82,
                                    orders: Math.ceil(discountAmount / 800) || 1,
                                    amount: discountAmount * 0.3
                                },
                                {
                                    name: 'Seasonal Offer',
                                    successRate: 68,
                                    orders: Math.ceil(discountAmount / 1000) || 1,
                                    amount: discountAmount * 0.1
                                }
                            ];
                        }

                        // Return just the summary if no detailed data
                        return {
                            Discount: discountAmount,
                            discount: discountAmount
                        };
                    })(),

                    // FOC from Table18
                    foc: (() => {
                        const focData = dataSet.Table18 || [];

                        if (focData.length > 0) {
                            const totalFocAmount = focData.reduce((sum, item) => sum + (item.FocAmount || 0), 0);
                            const totalFocOrders = focData.length;

                            return {
                                count: totalFocOrders,
                                amount: totalFocAmount,
                                orders: focData.map((item, index) => ({
                                    orderId: item.OrderNumber || `FOC-${String(index + 1).padStart(3, '0')}`,
                                    customer: item.CareOfName || 'FOC Customer',
                                    reference: 'FOC',
                                    amount: item.FocAmount || 0,
                                    items: 'FOC Items'
                                }))
                            };
                        }

                        // Fallback to payment mode FOC if Table18 is empty
                        const focPayment = dataSet.Table8?.find(item => item.PaymentMode === 'FOC');
                        if (focPayment && focPayment.OrderCount > 0) {
                            return {
                                count: focPayment.OrderCount,
                                amount: focPayment.TotalAmount,
                                orders: Array.from({ length: focPayment.OrderCount }, (_, i) => ({
                                    orderId: `FOC-${String(i + 1).padStart(3, '0')}`,
                                    customer: 'FOC Customer',
                                    reference: 'FOC',
                                    amount: focPayment.TotalAmount / focPayment.OrderCount,
                                    items: 'FOC Items'
                                }))
                            };
                        }

                        return { count: 0, amount: 0, orders: [] };
                    })(),

                    // Deleted orders from Table20
                    deletedOrders: (() => {
                        const deletedData = dataSet.Table20 || [];

                        if (deletedData.length > 0) {
                            const totalAmount = deletedData.reduce((sum, item) => sum + (item.CancelOrderAmount || 0), 0);

                            return {
                                count: deletedData.length,
                                amount: totalAmount,
                                orders: deletedData.map((item, index) => ({
                                    orderId: item.OrderNumber || `CANCEL-${String(index + 1).padStart(3, '0')}`,
                                    orderType: 'Cancelled Order',
                                    amount: item.CancelOrderAmount || 0,
                                    reason: item.OrderCancelReason || 'Customer cancelled',
                                    time: new Date().toLocaleTimeString(),
                                    status: 'Cancelled'
                                }))
                            };
                        }

                        // Fallback to cancelled status from Table1
                        const cancelledStatus = dataSet.Table1?.find(item =>
                            item.OrderStatus?.toLowerCase() === 'cancel' ||
                            item.OrderStatus?.toLowerCase() === 'cancelled'
                        );

                        if (cancelledStatus && cancelledStatus.OrderCount > 0) {
                            const count = cancelledStatus.OrderCount;
                            // Create mock deleted orders based on the count
                            const mockOrders = Array.from({ length: Math.min(count, 5) }, (_, i) => ({
                                orderId: `CANCEL-${String(i + 1).padStart(3, '0')}`,
                                orderType: ['Dine In', 'Delivery', 'Pickup'][Math.floor(Math.random() * 3)],
                                amount: Math.floor(Math.random() * 1000) + 500,
                                reason: ['Customer cancelled', 'Payment failed', 'Duplicate order', 'Out of stock'][Math.floor(Math.random() * 4)],
                                time: new Date().toLocaleTimeString()
                            }));

                            return {
                                count: count,
                                amount: mockOrders.reduce((sum, order) => sum + order.amount, 0),
                                orders: mockOrders
                            };
                        }

                        return { count: 0, amount: 0, orders: [] };
                    })(),

                    // Returns from Table22
                    returns: (() => {
                        const returnsData = dataSet.Table22 || [];

                        if (returnsData.length > 0) {
                            const totalAmount = returnsData.reduce((sum, item) => sum + (item.ReturnAmount || 0), 0);
                            const totalQuantity = returnsData.reduce((sum, item) => sum + (item.ReturnQuantity || 0), 0);

                            return {
                                totalAmount: totalAmount,
                                totalOrders: returnsData.length,
                                totalQuantity: totalQuantity,
                                returns: returnsData.map((item, index) => ({
                                    paymentMode: 'Sales Return',
                                    amount: item.ReturnAmount || 0,
                                    orders: 1,
                                    quantity: item.ReturnQuantity || 0,
                                    returnNumber: item.SalesReturnNumber || `SRTN-${String(index + 1).padStart(3, '0')}`,
                                    tax15: (item.ReturnAmount || 0) * 0.15,
                                    tax8: (item.ReturnAmount || 0) * 0.08,
                                    date: new Date().toLocaleDateString()
                                }))
                            };
                        }

                        // Fallback to summary SaleReturnAmount
                        const summary = dataSet.Table?.[0] || {};
                        const returnAmount = summary.SaleReturnAmount || summary.saleReturnAmount || 0;

                        if (returnAmount > 0) {
                            return {
                                totalAmount: returnAmount,
                                totalOrders: 1,
                                returns: [{
                                    paymentMode: 'Sale Returns',
                                    amount: returnAmount,
                                    orders: 1,
                                    tax15: returnAmount * 0.15,
                                    tax8: returnAmount * 0.08,
                                    date: new Date().toLocaleDateString()
                                }]
                            };
                        }

                        return {
                            totalAmount: 0,
                            totalOrders: 0,
                            returns: []
                        };
                    })(),
                };

                setDashboardData(transformedData);
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch data when filters change
    useEffect(() => {
        fetchDashboardData();
    }, [dateRange, orderTypeFilter, viewMode, appStore.CompanyId]);

    // Filter data based on order type filter
    const filteredData = dashboardData ? {
        ...dashboardData,
        orderTypes: orderTypeFilter === 'all'
            ? dashboardData.orderTypes
            : dashboardData.orderTypes.filter((ot) =>
                ot.name?.toLowerCase().includes(orderTypeFilter.toLowerCase())
            ),
    } : {};

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">

            {loading && (
                <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-white rounded-xl p-6 shadow-2xl flex items-center gap-3">
                        <div className="w-6 h-6 border-3 border-[#01796d] border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-slate-700 font-medium">Loading dashboard data...</span>
                    </div>
                </div>
            )}

            <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
                <FilterBar
                    dateRange={dateRange}
                    setDateRange={setDateRange}
                    orderTypeFilter={orderTypeFilter}
                    setOrderTypeFilter={setOrderTypeFilter}
                    viewMode={viewMode}
                    setViewMode={setViewMode}
                    onRefresh={() => fetchDashboardData()}
                    dashboardData={filteredData}
                />

                <KPICards data={filteredData} />

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <OrderSourceChart data={filteredData.orderSources} />
                    <PaymentModeChart data={filteredData.paymentModes} />
                    <OrderTypeChart data={filteredData.orderTypes} />
                    <TaxBreakdownChart data={filteredData.taxBreakdown} />
                </div>

                <HourlySalesChart data={filteredData.hourlySales} viewMode={viewMode} />

                <DiscountSuccessChart data={filteredData.discounts} />

                <PerformanceMetrics
                    avgOrders={filteredData.avgOrders}
                    waiterPerformance={filteredData.waiterPerformance}
                    riderPerformance={filteredData.riderPerformance}
                />

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <FOCSection data={filteredData.foc} />
                    <DeletedOrdersSection data={filteredData.deletedOrders} />
                    <ReturnsSection data={filteredData.returns} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <TopProductsSection data={filteredData.topProducts} />
                    <CategoriesChart data={filteredData.categories} />
                </div>
            </div>
        </div>
    );
}